import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authService } from '../services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'theme';

/** Retourne la préférence système courante ('dark' | 'light'). */
function getSystemPreference() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Lit la valeur initiale : localStorage en priorité, puis système. */
function resolveInitialTheme() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  return getSystemPreference();
}

/** Applique / retire la classe `dark` sur <html> et met à jour color-scheme. */
function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * @typedef {{ theme: 'light' | 'dark', setTheme: (t: 'light' | 'dark') => void, toggleTheme: () => void }} ThemeContextValue
 */

const ThemeContext = createContext(/** @type {ThemeContextValue | null} */ (null));
ThemeContext.displayName = 'ThemeContext';

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Fournit le thème dark/light à toute l'arborescence.
 *
 * - Lit la préférence depuis localStorage au mount.
 * - Bascule la classe `dark` sur <html>.
 * - Persiste chaque changement dans localStorage.
 * - Écoute les changements système (prefers-color-scheme) uniquement si
 *   l'utilisateur n'a pas défini de préférence manuelle.
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => resolveInitialTheme());

  // Applique le thème initial sans attendre un rendu.
  // On le fait aussi dans le state initializer pour éviter le flash (FOUC).
  useEffect(() => {
    applyTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  /**
   * Définit le thème manuellement.
   * Persiste en localStorage + synchronise en DB si l'utilisateur est connecté
   * (via présence du JWT dans sessionStorage — évite une dépendance à AuthProvider).
   */
  const setTheme = useCallback((newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') return;
    localStorage.setItem(STORAGE_KEY, newTheme);
    applyTheme(newTheme);
    setThemeState(newTheme);

    // Sync backend en fire-and-forget (pas bloquant pour l'UI)
    if (sessionStorage.getItem('auth_token')) {
      authService.updateMyTheme(newTheme).catch(() => {
        // Silencieux : le thème local est déjà appliqué
      });
    }
  }, []);

  /**
   * Applique une préférence venant du serveur (au login) sans déclencher
   * de sync DB — sinon on aurait une boucle (server → client → server).
   */
  const applyServerTheme = useCallback((serverTheme) => {
    if (serverTheme !== 'light' && serverTheme !== 'dark') return;
    localStorage.setItem(STORAGE_KEY, serverTheme);
    applyTheme(serverTheme);
    setThemeState(serverTheme);
  }, []);

  /** Bascule entre light et dark. */
  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  // Écoute les changements système — actif seulement si l'user n'a pas
  // de préférence manuelle stockée (localStorage vide).
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');

    function handleSystemChange(e) {
      // Ne suit le système que si aucune préférence manuelle n'est stockée.
      if (localStorage.getItem(STORAGE_KEY) !== null) return;
      const next = e.matches ? 'dark' : 'light';
      applyTheme(next);
      setThemeState(next);
    }

    mql.addEventListener('change', handleSystemChange);
    return () => mql.removeEventListener('change', handleSystemChange);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, applyServerTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Accède au contexte de thème.
 * Lance une erreur si utilisé hors de <ThemeProvider>.
 *
 * @returns {ThemeContextValue}
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error('useTheme doit être utilisé dans un <ThemeProvider>.');
  }
  return ctx;
}
